import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Text, Loader, Box, VerticalBox, Title } from '../../components';
import { COLORS, device } from '../../constants';
import {
  Description as ProjectDescriptionComponent,
  ProjectStores,
  ProjectIssues,
  ProjectResults,
  ProjectFeatures,
  Technologies,
  ProjectAchievements,
  ProjectTeam,
  RelatedProjects,
  ProjectExpertises,
} from './partials';
import { fetchProjectDetailsPage } from '../../services/project-details/projectService';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Tag } from '../../components/Tag/Tag';
import { TypesOfProject } from './partials/Description/TypesOfProject';
import { useTranslation } from 'react-i18next';
import { MotionProjectImage, MotionText } from '../../components/Motion';

export const ProjectDetails = () => {
  const { t } = useTranslation();
  const { projectName } = useParams<{ projectName: string }>();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['project-details', projectName],
    queryFn: async () => fetchProjectDetailsPage(projectName!),
    enabled: !!projectName,
  });

  useEffect(() => {
    if (data?.projectInfo.SectionImage) {
      const img = new Image();
      img.src = data.projectInfo.SectionImage;
      img.onload = async () => {
        try {
          await img.decode();
          setIsImageLoaded(true);
        } catch {
          setIsImageLoaded(false); 
        }
      };
      img.onerror = () => setIsImageLoaded(false);
    }
  }, [data]);
  

  const teamData = useMemo(() => {
    return data?.projectInfo.Team_Allocation === 'Implement from scratch'
      ? Object.entries(data?.projectInfo.Team)
          .filter(([_, value]) => value !== null && value > 0)
          .map(([key, value]) => ({
            ProjectRole: key,
            CountOfMembers: value ?? 0,
          }))
      : data?.projectInfo.TeamParts;
  }, [
    data?.projectInfo.Team_Allocation,
    data?.projectInfo.Team,
    data?.projectInfo.TeamParts,
  ]);

  if (isLoading) {
    return <Loader />;
  }

  if (error || !data) {
    return (
      <Box>
        <AbscentProject>
          <Text text="Project not found" />
        </AbscentProject>
      </Box>
    );
  }

  const { projectInfo: project, projectsList } = data;

  const {
    Name = 'Project Name',
    Description = 'No description available',
    SectionImage = '',
    Team_Allocation = 'Not specified',
    Type_of_Project = 'Not specified',
    Domain,
    Issues,
    Features,
    Expertises: expertisesList = [],
    Technologies: technologiesList = [],
    Links,
  } = project;

  const resolvedAllocation =
    Team_Allocation === 'Take part in development'
      ? t('utils.card.allocation.take_part')
      : t('utils.card.allocation.scratch');
  const domainName = Domain?.Name;
  const duration = project.Result?.Duration ?? 0;

  const storeLinks = Links
    ? Object.values(Links).filter((link): link is string => link !== null)
    : [];

  return (
    <>
      <ProjectHeader>
        <Box>
          <VerticalBox>
            <ProjectHeaderWrapper>
              <ProjectHeaderInfoWrapper>
                <TextWrapper>
                  <ProjectFacetsList>
                    {domainName ? (
                      <>
                        <Tag isAnimated={true}>
                          <Text
                            text={domainName}
                            color={COLORS.ROUTE_PATH}
                            size={14}
                            lineHeight={21}
                          />
                        </Tag>
                      </>
                    ) : null}
                    {technologiesList && technologiesList.length > 0 && (
                      <>
                        {technologiesList.map(tech => (
                          <Tag key={tech._id} isAnimated={true}>
                            <Text
                              text={tech.Name}
                              color={COLORS.ROUTE_PATH}
                              size={14}
                              lineHeight={21}
                            />
                          </Tag>
                        ))}
                      </>
                    )}
                    {expertisesList && expertisesList.length > 0 && (
                      <>
                        {expertisesList.map(tech => (
                          <Tag key={tech._id} isAnimated={true}>
                            <Text
                              text={tech.Name}
                              color={COLORS.ROUTE_PATH}
                              size={14}
                              lineHeight={21}
                            />
                          </Tag>
                        ))}
                      </>
                    )}
                  </ProjectFacetsList>
                  <ProjectHeaderDescriptionWrapper>
                    <Title
                      weight={600}
                      size={60}
                      lineHeight={90}
                      title={Name}
                      color={COLORS.BLACK_TITLE}
                    />
                    <Text
                      size={22}
                      lineHeight={28}
                      text={Description}
                      color={COLORS.BLACK_TITLE}
                    />
                  </ProjectHeaderDescriptionWrapper>
                </TextWrapper>

                <ProjectStores
                  storeLinks={storeLinks}
                  privateReason={data?.projectInfo.PrivateReason}
                />
              </ProjectHeaderInfoWrapper>

              <ProjectHeaderImage>
                {SectionImage && isImageLoaded && (
                  <MotionProjectImage
                    draggable={false}
                    src={SectionImage}
                    alt={`${Name} header image`}
                  />
                )}
              </ProjectHeaderImage>

            </ProjectHeaderWrapper>
          </VerticalBox>
        </Box>
      </ProjectHeader>

      <ProjectDescriptionComponent
        objList={[
          { 'Development Model': resolvedAllocation },
          {
            Framework:
              TypesOfProject[Type_of_Project as keyof typeof TypesOfProject],
          },
          { Domain: domainName },
          { Duration: `${duration} month${duration > 1 ? 's' : ''}` },
          {
            Team: `${project.TeamMembers} member${project.TeamMembers > 1 ? 's' : ''}`,
          },
          { "Client's Region": project.Region || 'Unknown' },
        ]}
      />

      {Issues?.length > 0 && <ProjectIssues issues={Issues} />}

      {project.Result?.ResultDescription?.length > 0 && (
        <ProjectResults resultDescriptions={project.Result.ResultDescription} />
      )}

      {Features?.length > 0 && (
        <ProjectFeatures
          features={Features.map(feature => ({
            _id: parseInt(feature._id, 10),
            Title: feature.Title,
            Caption: feature.Caption || '',
          }))}
        />
      )}

      {teamData && <ProjectTeam team={teamData} />}

      {Technologies?.length > 0 && (
        <Technologies
          objList={technologiesList.map(tech => ({
            _id: tech._id,
            Name: tech.Name || 'Unnamed Technology',
          }))}
        />
      )}
      {expertisesList?.length > 0 && (
        <ProjectExpertises
          expertises={expertisesList.map(exp => ({
            _id: exp._id,
            Name: exp.Name || 'Unnamed Expertises',
          }))}
        />
      )}
      {project.Result?.Achievements?.length > 0 && (
        <ProjectAchievements achievements={project.Result.Achievements} />
      )}

      <RelatedProjects projects={projectsList} />
    </>
  );
};
const ProjectHeader = styled.div`
  background-color: ${COLORS.GRAY_BG_COLOR};
`;
const ProjectHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 80px;
  @media screen and (${device.bsm}) {
    flex-direction: column;
    gap: 10px;
  }
`;
const ProjectHeaderInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 80px;
  @media screen and (${device.lg}) {
    gap: 40px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  @media screen and (${device.sm}) {
    gap: 20px;
    flex-direction: column-reverse;
  }
`;
const ProjectHeaderDescriptionWrapper = styled(MotionText)`
  display: flex;
  flex-direction: column;
  gap: 40px;
  @media screen and (${device.lg}) {
    > span {
      font-size: 37px;
      line-height: 50px;
    }
    > h4 {
      font-size: 20px;
      line-height: 28px;
    }
  }
  @media screen and (${device.sm}) {
    gap: 10px;
    > span {
      font-size: 26px;
      line-height: 34px;
    }
    > h4 {
      font-size: 16px;
      line-height: 22px;
    }
  }
`;

const ProjectFacetsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  @media screen and (${device.md}) {
    gap: 10px;
  }
`;
const ProjectHeaderImage = styled.div`
  flex: 1 0 40%;
  max-height: 700px;

  @media screen and (${device.md}) {
    max-height: 500px;
    margin-top: 10px;
  }
`;

const AbscentProject = styled.div`
  padding: 20px 0;
`;
